<template>
  <div>
    <div v-if="shownAwards.length > 0" class="business-intelligence-awards-list" :class="size">
      <BIBadge
        v-for="award in shownAwards"
        type="winner"
        :key="award.id"
        :source="source"
        :size="size"
        :data="award"
        :variant="getVariant(award)"
        :displayIndex="getIndex(award)"
        :hasWinner="hasWinner"
        @add-competitor="onAddCompetitorClicked"
        @edit-competitor="onEditCompetitorClicked"
        @remove-winner="onRemoveWinnerClicked"
        @action-succeeded="onActionSucceeded"
      >
        <template slot="icon">
          <BaseIcon name="award" />
        </template>
      </BIBadge>
      <v-popover
        placement="top"
        trigger="hover"
        popoverClass="business-intelligence__awards-list-tooltip"
        v-if="hiddenAwards.length > 0"
      >
        <span>+{{ hiddenAwards.length }}</span>
        <template v-slot:popover>
          <div>
            <div class="__award-item" v-for="(award, index) in hiddenAwards" :key="index">
              <span>{{ award.supplier_name }}</span>
              <span class="number-monospace">{{ displayCurrency(award.amount, 0) }}</span>
            </div>
          </div>
        </template>
      </v-popover>
    </div>
    <div v-else class="business-intelligence-awards-list" :class="size">
      <BIBadge :text="$t('page.business-intelligence.awards.pending')" type="winner" variant="muted" :size="size">
        <template slot="icon">
          <BaseIcon name="award" />
        </template>
      </BIBadge>
    </div>
  </div>
</template>
<script>
import { displayCurrency } from '@/util/currency-util';
import BaseIcon from '@/components/base/BaseIcon.vue';
import BIBadge from './BIBadge.vue';
import { rankIn, selectFeatured } from '@/util/bi-featured-entries';

export default {
  name: 'BIAwardsList',
  components: { BIBadge, BaseIcon },
  props: {
    awards: {
      type: Array,
      required: true,
      default: () => [],
    },
    size: {
      type: String,
      default: () => 'compact', // compact, full
    },
    limit: {
      type: Number,
      required: false,
    },
    currentSupplier: {
      type: String,
      required: false,
      default: null,
    },
    source: {
      type: String,
      required: false,
      default: null,
    },
    hasWinner: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  computed: {
    listLimit() {
      return this.limit ? this.limit : this.awards.length;
    },
    sortedAwards() {
      // sort from highest to lowest amount
      return [...this.awards].sort((a, b) => b.amount - a.amount);
    },
    officialAwards() {
      return this.sortedAwards ? this.sortedAwards.filter((award) => !award.is_manual_entry) : [];
    },
    manualEntries() {
      return this.sortedAwards ? this.sortedAwards.filter((award) => award.is_manual_entry) : [];
    },
    awardPool() {
      return this.officialAwards.length > 0 ? this.officialAwards : this.manualEntries;
    },
    shownAwards() {
      return selectFeatured(this.awardPool, this.listLimit);
    },
    hiddenAwards() {
      const shown = new Set(this.shownAwards);
      return this.awardPool.filter((award) => !shown.has(award));
    },
  },
  methods: {
    displayCurrency,
    displayAmount(amount) {
      if (amount === null || amount === undefined) {
        return 'N/A';
      }
      return this.formatNumToCurrency(amount, 0);
    },
    getIndex(award) {
      return rankIn(this.awardPool, award) + 1;
    },
    getVariant(award) {
      if (award.is_competitor) {
        return 'active';
      } else if (award.is_tenant) {
        return 'emphasis';
      }
      return 'default';
    },
    onActionSucceeded() {
      this.$emit('action-succeeded');
    },
    onAddCompetitorClicked(name) {
      this.$emit('add-competitor', name);
    },
    onEditCompetitorClicked(competitor, name) {
      this.$emit('edit-competitor', competitor, name);
    },
    onRemoveWinnerClicked(id) {
      this.$emit('remove-winner', id);
    },
  },
};
</script>

<style lang="scss">
.business-intelligence-awards-list {
  display: flex;
  min-width: 215px;
  width: 100%;
  gap: 8px;

  &.compact {
    flex-direction: row;
  }

  &.full {
    flex-direction: column;
  }
}

.business-intelligence__awards-list-tooltip {
  padding: 8px;
  font-size: $font-size-xsmall;
  background-color: $color-normal !important;
  color: $color-white !important;
  box-shadow: $shadow-tooltip;

  .popover-arrow {
    background-color: $color-normal !important;
  }

  .__award-item {
    display: flex;
    gap: 8px;
    justify-content: space-between;

    &:not(:last-child) {
      margin-bottom: 4px;
    }
  }
}
</style>
